import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Animated, LayoutChangeEvent } from 'react-native';
import { useTheme } from '../../theme/ThemeContext';
import { Ionicons } from '@expo/vector-icons';

export type IntroTab = 'text' | 'video';

interface IntroSegmentedControlProps {
  selectedTab: IntroTab;
  onTabChange: (tab: IntroTab) => void;
}

const IntroSegmentedControl: React.FC<IntroSegmentedControlProps> = ({
  selectedTab,
  onTabChange,
}) => {
  const { theme } = useTheme();
  const [containerWidth, setContainerWidth] = useState(0);
  const slideAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const segmentWidth = containerWidth > 0 ? (containerWidth - 8) / 2 : 0; // 8 = padding(4) * 2

  const getIndex = (tab: IntroTab): number => (tab === 'text' ? 0 : 1);

  useEffect(() => {
    if (segmentWidth === 0) return;
    const index = getIndex(selectedTab);

    // Animate indicator slide
    Animated.spring(slideAnim, {
      toValue: index * segmentWidth,
      useNativeDriver: true,
      tension: 80,
      friction: 10,
    }).start();

    // Pulse animation feedback
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 0.96,
        duration: 80,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 80,
        useNativeDriver: true,
      }),
    ]).start();
  }, [selectedTab, segmentWidth]);

  const handleLayout = (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    if (w > 0 && w !== containerWidth) {
      setContainerWidth(w);
      const sw = (w - 8) / 2;
      slideAnim.setValue(getIndex(selectedTab) * sw);
    }
  };

  return (
    <View
      style={[styles.container, { backgroundColor: theme.backgroundSecondary }]}
      onLayout={handleLayout}
    >
      {/* Animated Sliding Pill */}
      {segmentWidth > 0 && (
        <Animated.View
          style={[
            styles.indicator,
            {
              backgroundColor: theme.accent,
              transform: [
                { translateX: slideAnim },
                { scale: scaleAnim },
              ],
              width: segmentWidth,
            },
          ]}
        />
      )}

      {/* Text Tab */}
      <TouchableOpacity
        style={styles.segment}
        onPress={() => onTabChange('text')}
        activeOpacity={0.7}
      >
        <Ionicons
          name="document-text-outline"
          size={17}
          color={selectedTab === 'text' ? '#FFFFFF' : theme.textSecondary}
        />
        <Animated.Text
          style={[
            styles.segmentText,
            {
              color: selectedTab === 'text' ? '#FFFFFF' : theme.textSecondary,
              fontWeight: selectedTab === 'text' ? '700' : '600',
            },
          ]}
        >
          Text
        </Animated.Text>
      </TouchableOpacity>

      {/* Video Tab */}
      <TouchableOpacity
        style={styles.segment}
        onPress={() => onTabChange('video')}
        activeOpacity={0.7}
      >
        <Ionicons
          name="videocam-outline"
          size={18}
          color={selectedTab === 'video' ? '#FFFFFF' : theme.textSecondary}
        />
        <Animated.Text
          style={[
            styles.segmentText,
            {
              color: selectedTab === 'video' ? '#FFFFFF' : theme.textSecondary,
              fontWeight: selectedTab === 'video' ? '700' : '600',
            },
          ]}
        >
          Video
        </Animated.Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    borderRadius: 999,
    padding: 4,
    marginBottom: 20,
    position: 'relative',
    height: 46,
  },
  indicator: {
    position: 'absolute',
    left: 4,
    top: 4,
    bottom: 4,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 3,
    zIndex: 1,
  },
  segment: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
    zIndex: 2,
  },
  segmentText: {
    fontSize: 14,
    zIndex: 3,
  },
});

export default IntroSegmentedControl;
